import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { oauth } from '../../utils';
import { Shop } from '../../interfaces/Shop';
import '../styles/main.css';
import ShopList from './ShopList';
import { Loading } from '@commonsku/styles';
import { Alert } from '../../components/helpers';
import CommonskuAvatarIcon from '../../components/icons/CommonskuAvatarIcon';
import { AppState } from '../types';

const ShopifyApp = () => {
  const {
    shop: shopifyShopDomain,
    shopifyShop,
    initialShop = null,
    session = null,
    isLoggedIn = false,
  } = useSelector((state: AppState) => state.shopify || ({} as AppState['shopify']));
  const shops = useSelector((state: AppState) => Object.values(state.entities.shops || {}));
  const [selectedShop, setSelectedShop] = useState(initialShop);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const isConnected = selectedShop?.external_store_url === shopifyShopDomain;

  const onSelectShop = (shop: Shop) => {
    setSelectedShop(s => s?.shop_id === shop.shop_id ? null : shop);
  };

  const onConnectShop = () => {
    if (!selectedShop) {
      return;
    }

    setIsLoading(true);
    oauth('POST', 'shopify', {
      action: 'connect-shop',
      commonsku_shop_id: selectedShop.shop_id,
      shopify_shop_id: shopifyShop.id,
      shopify_shop_domain: shopifyShopDomain,
      shopify_shop_name: shopifyShop?.name ?? '',
      session,
    }).then(res => {
      setSelectedShop(res?.json?.shop ?? null);
      setIsLoading(false);
      setError(res?.json?.error ?? '');
    }).catch((res) => {
      setIsLoading(false);
      setError(res?.json?.error ?? 'Error connecting to shopify');
    });
  };

  const onDisconnectShop = () => {
    if (!selectedShop) {
      return;
    }

    setIsLoading(true);
    oauth('POST', 'shopify', {
      action: 'disconnect-shop',
      commonsku_shop_id: selectedShop?.shop_id,
      shopify_shop_id: shopifyShop?.id,
      shopify_shop_domain: shopifyShopDomain,
      shopify_shop_name: shopifyShop?.name ?? '',
      session,
    }).then(res => {
      setSelectedShop(res?.json?.shop ?? null);
      setIsLoading(false);
      setError(res?.json?.error ?? '');
    }).catch((res) => {
      setIsLoading(false);
      setError(res?.json?.error ?? 'Error disconnecting from shopify');
    });
  };

  const ConnectedShop = isConnected && !isLoading && (
    <div className="shopify-app__main__connected-shop-container">
      <div className="shopify-app__main__connected-shop-container__shop-avatar">
        <CommonskuAvatarIcon />
      </div>
      <div className="shopify-app__main__connected-shop-container__shop-info">
        <p className="shopify-app__main__connected-shop-container__shop-info-name">
          {selectedShop.shop_name}
        </p>
        <p className="shopify-app__main__connected-shop-container__shop-info-connected-desc">
          commonsku Shop connected
        </p>
      </div>
      <button className="shopify-app__main__connected-shop-container-button"
        onClick={onDisconnectShop}
      >Disconnect</button>
    </div>
  );

  const LoadingComponent = isLoading && (
    <div className="shopify-app__main__loading-component">
      <Loading />
    </div>
  );

  const ErrorComponent = error && (
    <div className="shopify-app__main__error-component">
      <Alert type="error">{error}</Alert>
    </div>
  );

  return (
    <div className="shopify-app">
      <header className="shopify-app__header">
        <p>App Setup</p>
      </header>
      <main className="shopify-app__main">
        <p className="shopify-app__main__title">Get Started with commonsku</p>
        {LoadingComponent}
        {ErrorComponent}
        {!isLoggedIn && !isLoading ? <LoginButton /> : null}
        {isLoggedIn ? ConnectedShop : null}
        {isLoggedIn && !isConnected && !isLoading && (
          <ShopList
            shops={shops}
            selectedShopId={selectedShop?.shop_id}
            onSelectShop={onSelectShop}
            onConnectShop={onConnectShop}
          />
        )}
      </main>
    </div>
  );
};

function LoginButton() {
  const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
  return (
    <div className='shopify-app__main__login-button-container'>
      <a className="shopify-app__main__login-button"
        href={'/login.php?return=' + returnUrl}
      >Login</a>
      <p className="shopify-app__main__login-button-description">
        Login to your commonsku account to continue
      </p>
    </div>
  );
}

export default ShopifyApp;
